import { GetObjectCommand, PutObjectCommand, type S3Client } from '@aws-sdk/client-s3'
import { FIRMWARE_LOCAL_NAME, parseOmf, sha256Hex, type OmfEntry } from '../omf'
import { FEATURED_IDENTITY_ID } from './adminS3'
import { fetchFirmware, type FirmwareTarget } from './fetchFirmware'
import {
  copyObject,
  getRequiredJsonObject,
  isNotFoundError,
  UPLOAD_BUCKET,
  withS3Client,
  type S3Io,
} from './s3'
import { isSemver, listVersionFolders } from './toyVersions'

const METADATA_FILENAME = 'metadata.json'
const COPY_CONCURRENCY = 8

// Stages a new toy version whose only change is a newer firmware build. The
// new folder is written as a sibling of the current version but is left
// DORMANT: versions-index.json and toy-index.json are deliberately untouched,
// so nothing serves the staged version until a future promote flips `latest`.

export type UpgradeToyFirmwareArgs = {
  // Version folder the toy currently serves (inspection's resolved version).
  fromVersion: string
  newVersion: string
  // Manifest filename inside the version folder (non-standard names exist,
  // e.g. mp3_player.omf on hand-made featured toys).
  manifestName: string
  target: FirmwareTarget
  onStep?: (step: string) => void
}

export function bumpMinor(version: string): string {
  const [major, minor] = version.split('.').map(Number)
  return `${major}.${minor + 1}.0`
}

async function getRequiredText(client: S3Client, key: string, what: string): Promise<string> {
  try {
    const res = await client.send(new GetObjectCommand({ Bucket: UPLOAD_BUCKET, Key: key }))
    if (!res.Body) throw new Error(`${what} is empty (${key})`)
    return await res.Body.transformToString()
  } catch (err) {
    if (isNotFoundError(err)) throw new Error(`${what} not found (${key})`)
    throw err
  }
}

// Rewrites only the FIRMWARE.BIN line of a manifest — new server filename and
// checksum — keeping every other line byte-for-byte intact (ordering, blank
// lines, checksum-less entries).
export function replaceFirmwareLine(
  manifestText: string,
  newServerFilename: string,
  newSha: string,
): string {
  return manifestText
    .split('\n')
    .map((line) => {
      const parts = line.split(' ')
      if (parts[1] !== FIRMWARE_LOCAL_NAME) return line
      const hasChecksum = parts.length >= 3
      return hasChecksum
        ? `${newServerFilename} ${FIRMWARE_LOCAL_NAME} ${newSha}`
        : `${newServerFilename} ${FIRMWARE_LOCAL_NAME}`
    })
    .join('\n')
}

export async function upgradeToyFirmware(
  io: S3Io,
  identityId: string,
  toyId: string,
  args: UpgradeToyFirmwareArgs,
): Promise<{ copiedFiles: number }> {
  const { fromVersion, newVersion, manifestName, target } = args
  const onStep = args.onStep ?? (() => {})

  // The Featured folder is publish-only: versions are minted in the source
  // user's folder and copied over, so the two version timelines can't diverge.
  if (identityId === FEATURED_IDENTITY_ID) {
    throw new Error(
      'Firmware updates are disabled in the Featured folder — update the source toy and copy the version over',
    )
  }
  if (!isSemver(newVersion)) {
    throw new Error(`Version must be MAJOR.MINOR.PATCH, got "${newVersion}"`)
  }

  onStep('Checking existing versions')
  const existing = await withS3Client(io, (client) =>
    listVersionFolders(client, identityId, toyId),
  )
  if (existing.includes(newVersion)) {
    throw new Error(`Version ${newVersion} already exists for this toy`)
  }

  // Download (not server-side copy) so the manifest checksum is computed from
  // the actual bytes rather than trusted from the build's metadata.
  onStep('Fetching firmware')
  const firmware = await fetchFirmware(io, target)
  const newSha = await sha256Hex(firmware.binary)

  return withS3Client(io, async (client) => {
    const fromPrefix = `${identityId}/${toyId}/${fromVersion}/`
    const newPrefix = `${identityId}/${toyId}/${newVersion}/`

    // Read and validate both source documents before writing anything, so a
    // broken source toy fails without leaving a half-staged version behind.
    onStep('Reading manifest')
    const manifestText = await getRequiredText(client, `${fromPrefix}${manifestName}`, 'Manifest')
    const manifest = parseOmf(manifestText)
    const firmwareEntry = manifest.entries.find(
      (e) => e.localFilename === FIRMWARE_LOCAL_NAME,
    )
    if (!firmwareEntry) {
      throw new Error(`Manifest has no ${FIRMWARE_LOCAL_NAME} entry`)
    }
    const payloadEntries = manifest.entries.filter((e) => e !== firmwareEntry)

    const metadata = await getRequiredJsonObject(
      client,
      UPLOAD_BUCKET,
      `${fromPrefix}${METADATA_FILENAME}`,
      METADATA_FILENAME,
    )

    // Same read-before-write caveat as the other admin tools: a concurrent
    // upload by the user could race this. Acceptable for a rare admin action.
    onStep('Copying files')
    const copyOne = (entry: OmfEntry) =>
      copyObject(
        client,
        UPLOAD_BUCKET,
        `${fromPrefix}${entry.serverFilename}`,
        `${newPrefix}${entry.serverFilename}`,
      )
    for (let i = 0; i < payloadEntries.length; i += COPY_CONCURRENCY) {
      await Promise.all(payloadEntries.slice(i, i + COPY_CONCURRENCY).map(copyOne))
    }

    // The new binary is stored under its own (build) filename — the old
    // version's firmware name is not carried over. The manifest line below is
    // rewritten to match, so folder and manifest stay consistent.
    onStep('Uploading firmware')
    await client.send(
      new PutObjectCommand({
        Bucket: UPLOAD_BUCKET,
        Key: `${newPrefix}${firmware.binaryName}`,
        Body: firmware.binary,
        ContentType: 'application/octet-stream',
      }),
    )

    onStep('Writing manifest')
    await client.send(
      new PutObjectCommand({
        Bucket: UPLOAD_BUCKET,
        Key: `${newPrefix}${manifestName}`,
        Body: new TextEncoder().encode(
          replaceFirmwareLine(manifestText, firmware.binaryName, newSha),
        ),
        ContentType: 'text/plain',
      }),
    )

    // metadata.json goes last — it marks the version folder complete.
    onStep('Writing metadata')
    metadata.version = newVersion
    // The staged version is a new artifact, so it gets its own creationTime
    // (the source version's value, if any, is deliberately replaced).
    metadata.creationTime = new Date().toISOString()
    if (typeof metadata.manifestUrl === 'string') {
      metadata.manifestUrl = metadata.manifestUrl.replace(
        `/${toyId}/${fromVersion}/`,
        `/${toyId}/${newVersion}/`,
      )
    }
    metadata.firmwareMetadata = firmware.metadata
    await client.send(
      new PutObjectCommand({
        Bucket: UPLOAD_BUCKET,
        Key: `${newPrefix}${METADATA_FILENAME}`,
        Body: new TextEncoder().encode(JSON.stringify(metadata, null, 2)),
        ContentType: 'application/json',
      }),
    )

    return { copiedFiles: payloadEntries.length }
  })
}
